import { useMemo, useState, type CSSProperties } from 'react';
import { useDropzone } from 'react-dropzone';
import { Box } from '~/commonComponents/Box';
import EditPencilIcon from '../../images/icons/EditPencil';
import GalleryIcon from '../../images/icons/GalleryIcon';
import { Button } from '@tempmee/react';
import { Colors } from '../../themes/colors';
import { ImageCropper } from '../ImageCropper';

interface IImageDropzoneProps {
  onComplete: (croppedImage: string) => void;
  dataCy?: string;
}

interface IFileWithPreview extends File {
  preview: string;
}

const baseStyle: CSSProperties = {
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  height: 280,
  borderWidth: 3,
  borderRadius: 140,
  borderColor: Colors.neutral_150,
  borderStyle: 'dashed',
  backgroundColor: Colors.white,
  outline: 'none',
  transition: 'border .24s ease-in-out',
};

const focusedStyle: CSSProperties = {
  borderColor: Colors.neutral_150,
};

const acceptStyle: CSSProperties = {
  borderColor: Colors.success_500,
};

const rejectStyle: CSSProperties = {
  borderColor: Colors.error_400,
};

export const ImageDropzone = ({ onComplete, dataCy }: IImageDropzoneProps) => {
  const [fullImage, setFullImage] = useState<IFileWithPreview | null>(null);
  const [showCropperModal, setShowCropperModal] = useState(false);
  const [croppedImage, setCroppedImage] = useState<string | null>(null);

  const { getRootProps, getInputProps, isDragAccept, isFocused, isDragReject, open } = useDropzone({
    accept: { 'image/*': [] },
    maxFiles: 1,
    noClick: true,
    maxSize: 2097152, // 2MB
    noKeyboard: true,
    onDrop: (acceptedFiles: File[]) => {
      const files = acceptedFiles.map((file) =>
        Object.assign(file, {
          preview: URL.createObjectURL(file),
        }),
      ) as IFileWithPreview[];
      setFullImage(files[0]);
      setShowCropperModal(true);
    },
  });

  const style = useMemo(
    () => ({
      ...baseStyle,
      ...(isFocused ? focusedStyle : {}),
      ...(isDragAccept ? acceptStyle : {}),
      ...(isDragReject ? rejectStyle : {}),
    }),
    [isFocused, isDragAccept, isDragReject],
  );

  const onCropComplete = (_croppedImage: string) => {
    setCroppedImage(_croppedImage);
    onComplete(_croppedImage);
    setShowCropperModal(false);
  };

  return (
    <>
      <input {...getInputProps()} style={{ display: 'none' }} />
      {!croppedImage ? (
        <Box width={320} alignItems="center" paddingVertical={4}>
          <div
            {...getRootProps({
              style: { ...style, height: 280, width: 280, borderRadius: '100%' },
            })}
          >
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
              }}
            >
              <GalleryIcon />
              <span
                style={{
                  fontSize: 16,
                  color: Colors.neutral_500,
                  fontFamily: 'Nunito',
                  display: 'flex',
                  justifyContent: 'center',
                  textAlign: 'center',
                  lineHeight: 1.75,
                  marginTop: 8,
                  marginBottom: 4,
                }}
              >
                Drag and drop to upload
                <br />
                OR
              </span>
              <Button data-cy={dataCy} type="button" variant="secondary" size="md" onClick={open}>
                Upload from Computer
              </Button>
            </div>
          </div>
        </Box>
      ) : (
        <>
          <Box width={320} alignItems="center" paddingTop={4}>
            <div
              {...getRootProps({
                style: {
                  ...style,
                  height: 280,
                  width: 280,
                  padding: 4,
                  borderRadius: '100%',
                },
              })}
            >
              <img
                alt="preview"
                src={croppedImage}
                style={styles.previewImage}
                data-cy={`${dataCy}-previewer`}
              />
            </div>
            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                gap: 48,
                marginTop: 8,
              }}
            >
              <div data-cy="btn-browse-image" onClick={open} style={styles.previewImageContainer}>
                <GalleryIcon size={24} />
                <span style={styles.previewImageText}>Browse Image</span>
              </div>
              <div onClick={() => setShowCropperModal(true)} style={styles.previewImageContainer}>
                <EditPencilIcon color={Colors.secondary_500} size={18} />
                <span style={styles.previewImageText}>Edit</span>
              </div>
            </div>
          </Box>
        </>
      )}

      {showCropperModal && fullImage && (
        <ImageCropper
          src={URL.createObjectURL(fullImage)}
          onComplete={onCropComplete}
          onClose={() => setShowCropperModal(false)}
        />
      )}
    </>
  );
};

const styles: Record<string, CSSProperties> = {
  previewImage: {
    width: 280,
    height: 280,
    borderRadius: 140,
    objectFit: 'cover',
  },
  previewImageContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: 4,
    cursor: 'pointer',
  },
  previewImageText: {
    fontSize: 16,
    color: Colors.neutral_600,
    fontFamily: 'Nunito',
  },
};
